import { useCachedTags } from "@tellescope/tanstack-query";
import { BlockProps } from "../../../../types";
import { Button, cn } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  Badge,
  type MultiSelectOption,
  Popover,
  PopoverTrigger,
  StringSelectionPopover,
  type StringSelectionPopoverOption,
} from "@tellescope/ui";
import {
  applyMongoFilter,
  compileFreeformFilterBlock,
  type FreeformFieldOption,
  type FreeformFilterBlock,
} from "@tellescope/utils";
import { useCallback, useMemo } from "react";
import { useBlock } from "../../../../hooks";
import { BlockHeader } from "../../BlockHeader";
import { BlockContent } from "../../BlockContent";
import { BlockEmptyState } from "../../BlockEmptyState";
import { TagsFilterPopover } from "./TagsFilterPopover";

export const TagsBlock = ({
  enduserId,
  info,
  className,
  viewId,
  blockId,
  blockIndex,
  onMaximize,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  alwaysShowButtons = false,
  contentClassName,
  blockLayout: providedBlockLayout,
}: BlockProps) => {
  const { t } = useTranslation("contacts");

  const {
    parsedInfo,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    enduserQuery: { data: allTags = [] },
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canDeleteBlock,
    canCreateBlock,
    updateEnduserMutation,
    canEditEnduser,
  } = useBlock({
    type: "information.tags",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout: providedBlockLayout,
    select: (enduser) => enduser.tags ?? [],
  });

  const filterBlock = useMemo<FreeformFilterBlock>(
    () => parsedInfo?.filter ?? { id: blockId ?? `${viewId}-${blockIndex}`, logic: "and", filters: [] },
    [parsedInfo?.filter, blockId, viewId, blockIndex]
  );

  const compiledFilter = useMemo(
    () => compileFreeformFilterBlock(filterBlock, { evalArraysByItem: true }),
    [filterBlock]
  );

  // Filtered here rather than in the query's `select` so the filter can read the debounce-aware
  // `parsedInfo`, which only exists once useBlock has run.
  const matchedTags = useMemo(
    () => (compiledFilter ? allTags.filter((tag) => applyMongoFilter({ tags: tag }, compiledFilter)) : allTags),
    [allTags, compiledFilter]
  );

  const onFilterChange = useCallback(
    (block: FreeformFilterBlock) => {
      updateInfo({ ...parsedInfo, id: parsedInfo?.id ?? crypto.randomUUID(), filter: block });
    },
    [parsedInfo, updateInfo]
  );

  const { data: tagOptions } = useCachedTags({
    model: "endusers",
  });

  const tagFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: "tags",
        label: t("Tags"),
        valueType: "tags",
        operations: ["equals", "does_not_equal", "contains", "does_not_contain"],
      },
    ],
    [t]
  );

  const tagSelectOptions = useMemo<MultiSelectOption[]>(() => {
    const selectedTags = filterBlock.filters.flatMap((filter) => (Array.isArray(filter.value) ? filter.value : []));
    const values = [...new Set([...(tagOptions ?? []), ...selectedTags])];
    return values.map((tag) => ({ label: tag, value: tag }));
  }, [tagOptions, filterBlock.filters]);

  const addTagOptions = useMemo<StringSelectionPopoverOption[]>(
    () =>
      (tagOptions ?? []).reduce<StringSelectionPopoverOption[]>((acc, tag) => {
        if (!allTags.includes(tag)) acc.push({ label: tag, value: tag });
        return acc;
      }, []),
    [tagOptions, allTags]
  );

  const addTag = useCallback(
    (tag: string) => {
      if (allTags.includes(tag)) return;
      updateEnduserMutation.mutateAsync({
        id: enduserId,
        tags: [...allTags, tag],
        _options: { replaceObjectFields: true },
      });
    },
    [allTags, enduserId, updateEnduserMutation]
  );

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditBlock && (
            <TagsFilterPopover
              filterBlock={filterBlock}
              fieldOptions={tagFieldOptions}
              selectOptions={tagSelectOptions}
              onFilterChange={onFilterChange}
            />
          )
        }
      />
      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-wrap content-start items-start gap-2", contentClassName)}
      >
        {matchedTags.length > 0 ? (
          matchedTags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
              {editMode && canEditEnduser && (
                <button
                  onClick={() =>
                    updateEnduserMutation.mutateAsync({
                      id: enduserId,
                      tags: allTags.filter((t) => t !== tag),
                      _options: {
                        replaceObjectFields: true,
                      },
                    })
                  }
                >
                  <X size={12} />
                </button>
              )}
            </Badge>
          ))
        ) : (
          <BlockEmptyState className="basis-full">
            {compiledFilter ? t("No matching tags") : t("No tags")}
          </BlockEmptyState>
        )}
        {editMode && canEditEnduser && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="font-normal basis-full justify-start" color="muted">
                {t("Add tags...")}
              </Button>
            </PopoverTrigger>
            <StringSelectionPopover
              className="w-80"
              align="start"
              options={addTagOptions}
              selectedValues={[]}
              onToggle={addTag}
              showCheckIndicators={false}
              searchPlaceholder={t("Search or add tags...")}
              emptyText={t("No tags")}
              creator={{
                render: (input) => t('Add "{{tag}}"', { tag: input }),
                isValidNewOption: (input) => {
                  const trimmed = input.trim();
                  return trimmed.length > 0 && !allTags.includes(trimmed);
                },
                onCreate: (value) => addTag(value.trim()),
              }}
            />
          </Popover>
        )}
      </BlockContent>
    </div>
  );
};
